// src/unicodex_ui/pages/VerifyPage.jsx
import React, { useState } from "react";
import "../styles/FormLayout.css";
import "../styles/VerifyPage.css";
import TopNav from "../components/TopNav";
import FooterBar from "../components/FooterBar";
import { verifyCode as verifyCodeApi } from "../services/identityApi";

function VerifyPage() {
  const [code, setCode] = useState("");
  const [location, setLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // backend result object

  async function handleSubmit(e) {
    e.preventDefault();

    const trimmedCode = code.trim();
    const trimmedLocation = location.trim();

    if (!trimmedCode) {
      setError("Please enter a UNI-CODEX code to verify.");
      return;
    }

    setSubmitting(true);
    setError("");
    setResult(null);

    try {
      const data = await verifyCodeApi({
        code: trimmedCode,
        locationText: trimmedLocation || undefined,
      });

      if (!data || data.ok !== true || !data.result) {
        setError("Unexpected response from verification service.");
        return;
      }

      setResult(data.result);
    } catch (err) {
      console.error("[VerifyPage] verify error:", err);
      setError("Unable to verify code at this time. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="ucx-body">
      <TopNav />

      <main className="ucx-main">
        <section className="ucx-section">
          <div className="ucx-container ucx-max-600">
            <h1>Verify a UNI-CODEX Product Code</h1>
            <p>
              Enter the full UNI-CODEX code as it appears on the product or
              packaging.
            </p>

            <form
              className="ucx-form"
              id="ucx-verify-form"
              onSubmit={handleSubmit}
            >
              <label htmlFor="code">UNI-CODEX Code</label>
              <input
                type="text"
                id="code"
                name="code"
                required
                placeholder="e.g. UCX-1234-ABCD-9"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />

              <label htmlFor="location">Location (optional)</label>
              <input
                type="text"
                id="location"
                name="location"
                placeholder="Store / City / Country"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />

              <button
                type="submit"
                className="ucx-btn ucx-btn-primary"
                disabled={submitting}
              >
                {submitting ? "Verifying…" : "Verify Code"}
              </button>
            </form>

            {(() => {
              // Decide result card style
              let resultClass = "ucx-verify-result";

              if (error) {
                resultClass += " ucx-verify-result-danger";
              } else if (result && result.result) {
                switch (result.result) {
                  case "genuine":
                    resultClass += " ucx-verify-result-success";
                    break;
                  case "expired":
                  case "recall":
                    resultClass += " ucx-verify-result-warning";
                    break;
                  case "blocked":
                  case "not_registered":
                  case "suspicious":
                    resultClass += " ucx-verify-result-danger";
                    break;
                  default:
                    break;
                }
              }

              // Derive heading based on result type
              const type = result && result.result;
              let heading = "Verification result will appear here.";

              if (type === "genuine") {
                heading = "This UNI-CODEX code is genuine.";
              } else if (type === "expired") {
                heading = "This UNI-CODEX code has expired.";
              } else if (type === "blocked") {
                heading = "This UNI-CODEX code has been blocked.";
              } else if (type === "recall") {
                heading = "This UNI-CODEX batch has been recalled.";
              } else if (type === "not_registered") {
                heading = "This UNI-CODEX code is not registered.";
              } else if (type === "suspicious") {
                heading = "This UNI-CODEX code is marked as suspicious.";
              }

              return (
                <div
                  id="ucx-verify-result"
                  className={resultClass}
                  data-mode={type || (error ? "error" : "placeholder")}
                >
                  {error ? (
                    <p className="ucx-muted">{error}</p>
                  ) : !result ? (
                    <p className="ucx-muted">
                      Enter a code above and click “Verify Code” to check its
                      status against the UNI-CODEX registry.
                    </p>
                  ) : (
                    <>
                      <h2 style={{ marginTop: 0, marginBottom: 8 }}>
                        {heading}
                      </h2>

                      {result.codeInfo && (
                        <p className="ucx-small">
                          <strong>Code:</strong> {result.codeInfo.code}{" "}
                          <br />
                          <strong>Verifications:</strong>{" "}
                          {result.codeInfo.verifiedCount}{" "}
                          {result.isFirstScan && "(first scan)"}
                          {result.isRepeatScan && "(repeat scan)"}
                        </p>
                      )}

                      {result.productInfo && (
                        <p className="ucx-small">
                          <strong>Product:</strong> {result.productInfo.name}
                          <br />
                          <strong>SKU:</strong> {result.productInfo.ucxSku}
                        </p>
                      )}

                      {result.batchInfo && (
                        <p className="ucx-small">
                          <strong>Batch:</strong> {result.batchInfo.ucxBatch}{" "}
                          ({result.batchInfo.batchSize} units)
                        </p>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            <p className="ucx-small ucx-top-margin">
              If you suspect a counterfeit or tampered product, retain the item
              and receipt and report it to the retailer and relevant
              authorities.
            </p>
          </div>
        </section>
      </main>

      <FooterBar />
    </div>
  );
}

export default VerifyPage;
